import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, MaskedLines, ParallaxImage, SectionLabel } from "./common";
import { useLenisReduced } from "../../lib/motionFlag";
import { resolveMedia } from "../../lib/api";

/* MARKETING PROBLEM */
export function Problem({ data }) {
  const d = data || {};
  return (
    <section id="problem" data-testid="section-problem" className="bg-[#F8F9FA] py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionLabel>The Problem</SectionLabel>
        <h2 className="font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-navy sm:text-6xl lg:text-7xl">
          <MaskedLines lines={d.heading_lines || []} lineClass="block" />
        </h2>
        <div className="mt-14 grid gap-12 lg:grid-cols-2">
          <div className="space-y-5">
            {(d.points || []).map((p, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="flex items-start gap-4 border-b border-navy/10 pb-5">
                  <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[#FF5722]" />
                  <p className="font-body text-lg text-navy/70 sm:text-xl">{p}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="flex flex-col justify-center">
            <Reveal>
              <p className="font-display text-3xl font-extrabold uppercase leading-tight tracking-tight text-navy sm:text-5xl">
                {d.conclusion}
              </p>
              <p className="mt-6 font-body text-base leading-relaxed text-navy/60 sm:text-lg">{d.sub}</p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

const DEFAULT_POS_BG = "https://static.prod-images.emergentagent.com/jobs/c6f51fbc-ba88-4cac-a357-6a477f785856/images/b616d5206c8582c14f25ce9546989584b747f8739f34f1c9709405a9336a1531.jpeg";

/* POSITIONING */
export function Positioning({ data }) {
  const d = data || {};
  const ref = useRef(null);
  const reduced = useLenisReduced();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], reduced ? ["0%", "0%"] : ["-8%", "8%"]);
  return (
    <section id="positioning" ref={ref} data-testid="section-positioning" className="relative overflow-hidden bg-navy py-28 md:py-40">
      <motion.img style={{ y, scale: 1.15 }} src={resolveMedia(d.bg_image || DEFAULT_POS_BG)} alt="" loading="lazy"
        className="parallax-layer absolute inset-0 h-full w-full object-cover opacity-25" />
      <div className="absolute inset-0 bg-gradient-to-b from-navy via-navy/70 to-navy" />
      <div className="relative mx-auto max-w-6xl px-5 text-center sm:px-8">
        <SectionLabel dark className="justify-center">Positioning</SectionLabel>
        <Reveal>
          <p className="mx-auto mb-8 max-w-2xl font-body text-sm font-semibold uppercase tracking-[0.15em] text-[#FF5722] sm:text-base">
            {d.small}
          </p>
        </Reveal>
        <h2 className="mx-auto max-w-5xl font-display text-3xl font-black uppercase leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
          {d.big}
        </h2>
      </div>
    </section>
  );
}

/* THINK / CREATE / REACH / CONVERT / GROW (The Method) */
export function Journey({ data }) {
  const d = data || {};
  const steps = d.steps || [];
  return (
    <section id="journey" data-testid="section-journey" className="bg-sand py-16 md:py-20">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionLabel>{d.heading || "The Method"}</SectionLabel>
      </div>
      <div className="mx-auto mt-6 max-w-7xl px-5 sm:px-8">
        {steps.map((st, i) => (
          <div key={i}>
            {i > 0 && (
              <div aria-hidden className="flex justify-center" data-testid={`journey-connector-${i}`}>
                <span className="h-10 w-px bg-gradient-to-b from-navy/15 via-[#FF5722] to-navy/15 md:h-14" />
              </div>
            )}
            <JourneyRow step={st} flip={i % 2 === 1} index={i} />
          </div>
        ))}
      </div>
    </section>
  );
}

function JourneyRow({ step, flip, index = 0 }) {
  return (
    <div className="grid items-center gap-6 py-6 md:grid-cols-2 md:gap-14 md:py-8">
      <Reveal className={flip ? "md:order-2" : ""}>
        <div>
          <div className="flex items-center gap-3 mb-3">
            <span className="h-2 w-2 rounded-full bg-[#FF5722]" />
            <span className="h-px w-8 bg-[#FF5722]/40" />
            <span className="font-body text-xs font-bold uppercase tracking-[0.2em] text-[#FF5722]">The Method</span>
          </div>
          <h3 className="font-display text-4xl font-extrabold uppercase tracking-tight text-navy md:text-6xl">{step.title}</h3>
        </div>
        <p className="mt-4 max-w-md font-body text-lg text-navy/60 md:text-xl leading-relaxed whitespace-pre-line">{step.desc}</p>
        {step.body && (
          <p className="mt-3 max-w-md font-body text-base text-navy/60 leading-relaxed whitespace-pre-line">{step.body}</p>
        )}
      </Reveal>
      <div className={flip ? "md:order-1" : ""}>
        <ParallaxImage src={step.image} alt={step.title} className="aspect-[16/11] w-full md:aspect-[16/10]" speed={0.12} testId={`journey-img-${index + 1}`} />
      </div>
    </div>
  );
}

/* 20+ YEARS LEGACY */
export function Experience({ data }) {
  const d = data || {};
  const imgs = d.images || [];
  return (
    <section id="experience" data-testid="section-experience" className="relative overflow-hidden bg-[#0E162D] py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <SectionLabel dark>Legacy</SectionLabel>
            <Reveal>
              <div className="flex items-end gap-4">
                <span className="font-display text-[7rem] font-black leading-[0.8] text-[#FF5722] sm:text-[11rem]">{d.number}</span>
              </div>
              <h2 className="font-display text-4xl font-extrabold uppercase tracking-tight text-white sm:text-5xl">{d.title}</h2>
              <p className="mt-4 font-body text-lg tracking-wide text-white/50">{d.sub}</p>
            </Reveal>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {imgs.map((im, i) => (
              <ParallaxImage key={i} src={im} className={`w-full ${i % 2 === 0 ? "aspect-[3/4] mt-0" : "aspect-[3/4] mt-8"}`} speed={0.1 + (i % 2) * 0.08} testId={`experience-img-${i}`} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
